import type { InferResponseType } from 'hono/client'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch'

import { cn } from '@/lib/utils'
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
} from '@/components/ui/pagination'
import { ScrollArea } from '@/components/ui/scroll-area'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'
import { MarkdownRenderer } from '@/components/markdown-renderer'
import { QuerySpinner } from '@/components/spinner/query-spinner'
import { apiClient } from '@/apiClient'
import { useGetContentByPage } from '@/features/document-contents/hooks/use-get-content-by-page.ts'
import { Thumbnail } from '@/features/files/thumbnail'

// Type for the single document response from the API
type DocumentWithDetails = InferResponseType<
  (typeof apiClient.documents)[':documentId']['$get']
>['data']

interface PreviewTabContentProps {
  document: DocumentWithDetails | undefined
  previewMode: 'image' | 'markdown'
  pageNum: number
  markdown: string
  getDocumentContentQuery: ReturnType<typeof useGetContentByPage>
  onPrevPage: () => void
  onNextPage: () => void
  onPreviewModeChange: (mode: 'image' | 'markdown') => void
  isMobile?: boolean
}

export function PreviewTabContent({
  document,
  previewMode,
  pageNum,
  markdown,
  getDocumentContentQuery,
  onPrevPage,
  onNextPage,
  onPreviewModeChange,
  isMobile = false,
}: PreviewTabContentProps) {
  return (
    <>
      <div className="flex min-h-1 grow rounded-md border">
        {previewMode === 'image' ? (
          <TransformWrapper>
            <TransformComponent
              wrapperClass={cn('flex !size-full min-h-1 max-w-full grow')}
              contentClass={cn('flex !size-full min-h-1 max-w-full grow')}
            >
              <Thumbnail
                className="mx-auto h-full max-w-full object-contain"
                fileId={
                  document?.jobs?.[0]?.thumbnails_processing?.status ===
                  'completed'
                    ? document.file_id
                    : null
                }
                res="high"
                pageNum={pageNum}
                pageCount={document?.num_pages ?? 1}
              />
            </TransformComponent>
          </TransformWrapper>
        ) : (
          <ScrollArea className="flex size-full items-center justify-center bg-white p-4">
            <QuerySpinner query={getDocumentContentQuery}>
              <MarkdownRenderer children={markdown} />
            </QuerySpinner>
          </ScrollArea>
        )}
      </div>

      <div
        className={cn(
          'mt-4 flex items-center justify-center',
          isMobile &&
            'flex-col gap-4 sm:flex-row sm:items-center sm:justify-between'
        )}
      >
        <Pagination className={isMobile ? 'justify-center' : 'justify-start'}>
          <PaginationContent>
            <PaginationItem>
              <PaginationLink onClick={onPrevPage}>
                <ChevronLeft className="size-4" />
              </PaginationLink>
            </PaginationItem>
            <span className="text-sm">
              Pagina: {pageNum} di {document?.num_pages}
            </span>
            <PaginationLink onClick={onNextPage}>
              <ChevronRight className="size-4" />
            </PaginationLink>
          </PaginationContent>
        </Pagination>

        <ToggleGroup
          type="single"
          value={previewMode}
          className={isMobile ? 'justify-center' : undefined}
        >
          <ToggleGroupItem
            value="image"
            onClick={() => onPreviewModeChange('image')}
          >
            immagini
          </ToggleGroupItem>
          <ToggleGroupItem
            value="markdown"
            onClick={() => onPreviewModeChange('markdown')}
          >
            testo
          </ToggleGroupItem>
        </ToggleGroup>
      </div>
    </>
  )
}
